// src/components/dashboard/MarqetaConnectForm.tsx
//
// The one place a Marqeta program credential is ever entered. HTTP-only
// by design (buildlog KDpbH5GMgEV7IpaZqLyY) — posts once to
// POST /dashboard/connect/marqeta, never becomes MCP tool input.
//
// Uses Dialog, not Drawer — a credential paste-in is a small, self-
// contained, deliberate interaction (two fields, submit), not browsable
// or swipeable content. Dialog is the right surface specifically for
// the API-key connector kind (Marqeta, and any future connector of the
// same kind) — NOT a universal "connect" surface. Search Console's
// OAuth kind stays on its existing redirect-based UX (SearchConsoleCard)
// unchanged; a full-page redirect cannot and should not live inside a
// Dialog. See buildlog for the kind-vs-UI-shape reasoning.
// IMPORTANT — this file's two POST calls (handleConnect, handleDisconnect)
// MUST use authedFetchRaw with a full /dashboard/... path, NOT authedFetch
// (which prepends /dashboard/api and would silently 200-with-HTML against
// a nonexistent route — confirmed live, broken for an unknown period
// before being found and fixed). /connect/marqeta lives on dashboardRouter,
// not apiRouter — see dashboard.ts. If you add a new connector form
// following this one as a template, check which router the target route
// is actually mounted on before picking a helper.

import React, { useState, useEffect } from 'react';
import { authedFetch, authedFetchRaw } from '../../services/dashboardApi';
import { Loader2Icon, CheckCircle2Icon, ShieldAlertIcon } from 'lucide-react';
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader,
  DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';

interface MarqetaConnectState {
  connected: boolean;
  connectedAt: string | null;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ---- Connect dialog — the only place a credential is entered --------

function ConnectDialog({ onConnected }: { onConnected: () => void }) {
  const [open, setOpen] = useState(false);
  const [applicationToken, setApplicationToken] = useState('');
  const [adminAccessToken, setAdminAccessToken] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = applicationToken.trim() && adminAccessToken.trim() && !isSaving;

  const handleConnect = async () => {
  if (!canSubmit) return;
  setIsSaving(true);
  setError(null);
  try {
    const res = await authedFetchRaw('/dashboard/connect/marqeta', {
      method: 'POST',
      body: JSON.stringify({
        applicationToken: applicationToken.trim(),
        adminAccessToken: adminAccessToken.trim(),
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to connect Marqeta.');
    }
    setApplicationToken('');
    setAdminAccessToken('');
    setOpen(false);
    onConnected();
  } catch (err: any) {
    setError(err.message || 'Could not connect Marqeta.');
  } finally {
    setIsSaving(false);
  }
};

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button className="bg-brand-accent text-white hover:bg-brand-accentHover" />}>
        Connect
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Marqeta</DialogTitle>
          <DialogDescription>
            Paste your program's tokens from your Marqeta dashboard. Stored encrypted — never shared
            with anyone else.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="space-y-2">
          <input
            type="password"
            value={applicationToken}
            onChange={(e) => setApplicationToken(e.target.value)}
            placeholder="Application token"
            autoComplete="off"
            className="w-full rounded-lg border border-brand-border px-3 py-2 text-sm"
          />
          <input
            type="password"
            value={adminAccessToken}
            onChange={(e) => setAdminAccessToken(e.target.value)}
            placeholder="Admin access token"
            autoComplete="off"
            className="w-full rounded-lg border border-brand-border px-3 py-2 text-sm"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={handleConnect}
            className="bg-brand-accent text-white hover:bg-brand-accentHover"
          >
            {isSaving ? <Loader2Icon className="inline size-4 animate-spin" /> : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Disconnect dialog — same surface, same reasoning ----------------

function DisconnectDialog({ onDisconnected }: { onDisconnected: () => void }) {
  const [open, setOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDisconnect = async () => {
  setIsDisconnecting(true);
  setError(null);
  try {
    const res = await authedFetchRaw('/dashboard/connect/marqeta/disconnect', { method: 'POST' });
    if (!res.ok) throw new Error('Failed to disconnect.');
    setOpen(false);
    onDisconnected();
  } catch (err: any) {
    setError(err.message || 'Could not disconnect.');
  } finally {
    setIsDisconnecting(false);
  }
};

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="ghost" />}>
        Disconnect
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disconnect Marqeta</DialogTitle>
          <DialogDescription>
            <span className="flex items-start gap-1.5">
              <ShieldAlertIcon className="size-4 shrink-0 text-amber-600 mt-0.5" />
              This only removes FDW's stored copy — it does not revoke access on your Marqeta
              program. To fully cut off access, rotate your Marqeta program credentials directly
              in your Marqeta dashboard.
            </span>
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={isDisconnecting}
            onClick={handleDisconnect}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {isDisconnecting ? <Loader2Icon className="inline size-4 animate-spin" /> : 'Confirm disconnect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Status card — unchanged shape, buttons now open dialogs ---------

export const MarqetaConnectForm: React.FC = () => {
  const [state, setState] = useState<MarqetaConnectState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await authedFetch('/entity-status');
      if (!res.ok) throw new Error('Failed to load connection status.');
      const data = await res.json();
      setState(data.marqetaConnect ?? { connected: false, connectedAt: null });
    } catch (err: any) {
      setError(err.message || 'Could not load Marqeta connection status.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading…</p>;

  return (
    <div className="rounded-xl border border-brand-border bg-brand-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-brand-black">Marqeta</span>
        {state?.connected ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">
            <CheckCircle2Icon className="size-3.5" /> Connected
          </span>
        ) : (
          <span className="rounded-full bg-brand-surface px-2 py-0.5 text-xs text-brand-gray">
            Not connected
          </span>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {state?.connected ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-brand-gray">
            Connected {state.connectedAt ? formatDate(state.connectedAt) : ''}.
          </p>
          <DisconnectDialog onDisconnected={load} />
        </div>
      ) : (
        <ConnectDialog onConnected={load} />
      )}
    </div>
  );
};